"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.PhoneInput = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const CountryData_1 = require("../../components/PhoneInput/CountryData");
const lodash_1 = require("lodash");
const icons_material_1 = require("@mui/icons-material");
const theme_1 = require("../../theme");
const CountrySelector_1 = require("../../components/CountrySelector");
const Typography_1 = require("../../components/Typography");
const classnames_1 = require("classnames");
const usePrevProp_1 = require("../../components/PhoneInput/hooks/usePrevProp");
const material_1 = require("@mui/material");
const PhoneInput_styles_1 = require("../../components/PhoneInput/PhoneInput.styles");
const s_lang_1 = require("../../hooks/s_lang");
const PhoneInput = ({ alwaysDefaultMask = false, areaCodes = null, autoFormat = true, containerClass = '', containerStyle = {}, copyNumbersOnly = true, countryCodeEditable = true, defaultMask = '... ... ... ... ..', disableCountryCode = false, disableCountryGuess = false, disableDropdown = false, disableInitialCountryGuess = false, drawerTitle = 'Select your country', enableAreaCodeStretch = false, enableAreaCodes = true, enableLongNumbers = false, enableSearch = false, enableTerritories = false, excludeCountries = [], inputClass = '', inputProps, inputStyle = {}, isValid = true, jumpCursorToEnd = false, keys = {
    UP: 38,
    DOWN: 40,
    RIGHT: 39,
    LEFT: 37,
    ENTER: 13,
    ESC: 27,
    PLUS: 43,
    A: 65,
    Z: 90,
    SPACE: 32,
    TAB: 9,
}, lang = 'en', masks = null, onEnterKeyPress = null, onChange, onlyCountries = [], placeholder = '1 (702) 123-4567', preferredCountries = [], preserveOrder = [], priority = null, prefix = '+', regions = '', showDropdown = false, value = '', country = '', buttonClass = '', disabled = false, forceLtr, onBlur, onClick, onFocus, onMount, onKeyDown, buttonStyle = {}, hideClearButton, searchInputPlaceHolder = 'Search for countries', error, defaultErrorMessage, }) => {
    const [showDropdownState, setShowDropdownState] = (0, react_1.useState)(showDropdown);
    const [formattedNumber, setFormattedNumber] = (0, react_1.useState)('');
    const [onlyCountriesState, setOnlyCountriesState] = (0, react_1.useState)([]);
    const [preferredCountriesState, setPreferredCountriesState] = (0, react_1.useState)([]);
    const [hiddenAreaCodesState, setHiddenAreaCodesState] = (0, react_1.useState)([]);
    const [selectedCountry, setSelectedCountry] = (0, react_1.useState)();
    const [highlightCountryIndex, setHighlightCountryIndex] = (0, react_1.useState)(0);
    const [queryString, setQueryString] = (0, react_1.useState)('');
    const [freezeSelection, setFreezeSelection] = (0, react_1.useState)(false);
    const [searchValue, setSearchValue] = (0, react_1.useState)('');
    const [caretPosition, setCaretPosition] = (0, react_1.useState)(0);
    const [oldCaretPosition, setOldCaretPosition] = (0, react_1.useState)(0);
    const [oldFormattedText] = (0, react_1.useState)('');
    const oldCountry = (0, usePrevProp_1.default)(country);
    const { reactTelInputStyles, formControlStyles, flagDropdownStyles } = (0, PhoneInput_styles_1.default)({ error, forceLtr });
    const dropdownContainerRef = (0, react_1.useRef)();
    const numberInputRef = (0, react_1.useRef)();
    const { getLocals } = (0, s_lang_1.phoneInputHook)();
    const formatNumber = (text, _country) => {
        if (!_country)
            return text;
        const { format } = _country;
        let pattern;
        if (disableCountryCode) {
            pattern = format.split(' ');
            pattern.shift();
            pattern = pattern.join(' ');
        }
        else if (enableAreaCodeStretch && _country.isAreaCode) {
            pattern = format.split(' ');
            pattern[1] = pattern[1].replace(/\.+/, ''.padEnd(_country.areaCodeLength, '.'));
            pattern = pattern.join(' ');
        }
        else {
            pattern = format;
        }
        if (!text || text.length === 0) {
            return disableCountryCode ? '' : prefix;
        }
        if ((text && text.length < 2) || !pattern || !autoFormat) {
            return disableCountryCode ? text : prefix + text;
        }
        const formattedObject = (0, lodash_1.reduce)(pattern, (acc, character) => {
            if (acc.remainingText.length === 0) {
                return acc;
            }
            if (character !== '.') {
                return {
                    formattedText: acc.formattedText + character,
                    remainingText: acc.remainingText,
                };
            }
            const [head, ...tail] = acc.remainingText;
            return {
                formattedText: acc.formattedText + head,
                remainingText: tail,
            };
        }, {
            formattedText: '',
            remainingText: text.split(''),
        });
        let _formattedNumber;
        if (enableLongNumbers) {
            _formattedNumber = formattedObject.formattedText + formattedObject.remainingText.join('');
        }
        else {
            _formattedNumber = formattedObject.formattedText;
        }
        if (_formattedNumber.includes('(') && !_formattedNumber.includes(')'))
            _formattedNumber += ')';
        return _formattedNumber;
    };
    const updateCountry = () => {
        let newSelectedCountry;
        if (typeof country === 'number' || /^\d/.test(country)) {
            newSelectedCountry = onlyCountriesState.find((o) => o.dialCode === +country);
        }
        else {
            newSelectedCountry = onlyCountriesState.find((o) => o.iso2 === country);
        }
        if (newSelectedCountry && newSelectedCountry.dialCode) {
            setSelectedCountry(newSelectedCountry);
            setFormattedNumber(disableCountryCode ? '' : formatNumber(newSelectedCountry.dialCode, newSelectedCountry));
        }
    };
    const guessSelectedCountry = (0, react_1.useCallback)((inputNumber) => {
        if (!enableAreaCodes) {
            const mainCode = hiddenAreaCodesState.find((_country) => (0, lodash_1.startsWith)(inputNumber, _country.dialCode) && _country.mainCode);
            if (mainCode) {
                return mainCode;
            }
        }
        const secondBestGuess = onlyCountriesState.find((o) => o.iso2 === country);
        if (inputNumber.trim() === '')
            return secondBestGuess;
        const bestGuess = onlyCountriesState.reduce((_selectedCountry, _country) => {
            if ((0, lodash_1.startsWith)(inputNumber, _country.dialCode)) {
                if (_country.dialCode.length > _selectedCountry.dialCode.length) {
                    return _country;
                }
                if (_country.dialCode.length === _selectedCountry.dialCode.length &&
                    _country.priority < _selectedCountry.priority) {
                    return _country;
                }
            }
            return _selectedCountry;
        }, { dialCode: '', priority: 10001 });
        if (!bestGuess.name)
            return secondBestGuess;
        return bestGuess;
    }, [enableAreaCodes, onlyCountriesState, hiddenAreaCodesState, country]);
    const getCountryData = () => {
        if (!selectedCountry)
            return {};
        return {
            name: selectedCountry.name || '',
            dialCode: selectedCountry.dialCode || '',
            countryCode: selectedCountry.iso2 || '',
            format: selectedCountry.format || '',
        };
    };
    (0, react_1.useEffect)(() => {
        const { _onlyCountries, _preferredCountries, _hiddenAreaCodes } = new CountryData_1.default({
            enableAreaCodes,
            enableTerritories,
            regions,
            onlyCountries,
            preferredCountries,
            excludeCountries,
            preserveOrder,
            masks,
            priority,
            areaCodes,
            localization: getLocals(lang),
            prefix,
            defaultMask,
            alwaysDefaultMask,
        });
        const inputNumber = value ? value.replace(/\D/g, '') : '';
        let countryGuess;
        if (disableInitialCountryGuess) {
            countryGuess = 0;
        }
        else if (inputNumber.length > 1) {
            countryGuess = guessSelectedCountry(inputNumber.substring(0, 6)) || 0;
        }
        else if (country) {
            countryGuess = _onlyCountries.find((o) => o.iso2 === country) || 0;
        }
        else {
            countryGuess = 0;
        }
        const dialCode = inputNumber.length < 2 && countryGuess && !(0, lodash_1.startsWith)(inputNumber, countryGuess.dialCode)
            ? countryGuess.dialCode
            : '';
        const _formattedNumber = inputNumber === '' && countryGuess === 0
            ? ''
            : formatNumber((disableCountryCode ? '' : dialCode) + inputNumber, countryGuess.name ? countryGuess : undefined);
        const _highlightCountryIndex = _onlyCountries.findIndex((o) => o === countryGuess);
        setShowDropdownState(showDropdown);
        setFormattedNumber(_formattedNumber);
        setOnlyCountriesState(_onlyCountries);
        setPreferredCountriesState(_preferredCountries);
        setHiddenAreaCodesState(_hiddenAreaCodes);
        setSelectedCountry(countryGuess);
        setHighlightCountryIndex(_highlightCountryIndex);
        setQueryString('');
        setFreezeSelection(false);
        setSearchValue('');
        onMount === null || onMount === void 0 ? void 0 : onMount(formattedNumber.replace(/[^0-9]+/g, ''), getCountryData(), formattedNumber);
    }, [lang]);
    (0, react_1.useEffect)(() => {
        if (oldCountry !== country) {
            updateCountry();
        }
    }, [value, country]);
    const getProbableCandidate = (0, react_1.useCallback)(() => {
        if (!queryString || queryString.length === 0) {
            return null;
        }
        const probableCountries = onlyCountriesState.filter((_country) => {
            return (0, lodash_1.startsWith)(_country.name.toLowerCase(), queryString.toLowerCase());
        }, this);
        return probableCountries[0];
    }, [onlyCountriesState, queryString]);
    const cursorToEnd = () => {
        const input = numberInputRef.current;
        input.focus();
        let len = input.value.length;
        if (input.value.charAt(len - 1) === ')')
            len -= 1;
        input.setSelectionRange(len, len);
    };
    const concatPreferredCountries = (_preferredCountries, _onlyCountries) => {
        if (_preferredCountries.length > 0) {
            return [...new Set(_preferredCountries.concat(_onlyCountries))];
        }
        return _onlyCountries;
    };
    const handleFlagDropdownClick = (e) => {
        e.preventDefault();
        if (!showDropdown && disabled)
            return;
        const allCountries = concatPreferredCountries(preferredCountriesState, onlyCountriesState);
        const _highlightCountryIndex = allCountries.findIndex((o) => o.dialCode === selectedCountry.dialCode && o.iso2 === selectedCountry.iso2);
        setShowDropdownState(!showDropdownState);
        setHighlightCountryIndex(_highlightCountryIndex);
    };
    const handleInput = (e) => {
        const { value: inputVal } = e.target;
        let _formattedNumber = disableCountryCode ? '' : prefix;
        let newSelectedCountry = selectedCountry;
        if (!countryCodeEditable) {
            const mainCode = newSelectedCountry.hasAreaCodes
                ? onlyCountriesState.find((o) => o.iso2 === newSelectedCountry.iso2 && o.mainCode).dialCode
                : newSelectedCountry.dialCode;
            const updatedInput = prefix + mainCode;
            if (inputVal.slice(0, updatedInput.length) !== updatedInput)
                return;
        }
        if (inputVal === prefix) {
            if (onChange)
                onChange({ value: '', data: getCountryData(), event: e, formattedValue: '' });
            setFormattedNumber('');
            return;
        }
        if (inputVal.replace(/\D/g, '').length > 15) {
            if (enableLongNumbers === false)
                return;
            if (typeof enableLongNumbers === 'number') {
                if (inputVal.replace(/\D/g, '').length > enableLongNumbers)
                    return;
            }
        }
        if (inputVal === formattedNumber)
            return;
        if (e.preventDefault) {
            e.preventDefault();
        }
        if (onChange)
            e.persist();
        if (inputVal.length > 0) {
            const inputNumber = inputVal.replace(/\D/g, '');
            if (!freezeSelection || (!!selectedCountry && selectedCountry.dialCode.length > inputNumber.length)) {
                if (disableCountryGuess) {
                    newSelectedCountry = selectedCountry;
                }
                else {
                    newSelectedCountry = guessSelectedCountry(inputNumber.substring(0, 6)) || selectedCountry;
                }
                setFreezeSelection(false);
            }
            _formattedNumber = formatNumber(inputNumber, newSelectedCountry);
            newSelectedCountry = newSelectedCountry.dialCode ? newSelectedCountry : selectedCountry;
        }
        setFormattedNumber(_formattedNumber);
        setSelectedCountry(newSelectedCountry);
    };
    (0, react_1.useEffect)(() => {
        const diff = oldFormattedText.length - formattedNumber.length;
        setOldCaretPosition(caretPosition);
        if (diff > 0) {
            setCaretPosition((prev) => prev - diff);
        }
        const lastChar = formattedNumber.charAt(formattedNumber.length - 1);
        if (lastChar === ')') {
            numberInputRef.current.setSelectionRange(formattedNumber.length - 1, formattedNumber.length - 1);
        }
        else if (caretPosition > 0 && oldFormattedText.length >= formattedNumber.length) {
            numberInputRef.current.setSelectionRange(caretPosition, caretPosition);
        }
        else if (oldCaretPosition < oldFormattedText.length) {
            numberInputRef.current.setSelectionRange(oldCaretPosition, oldCaretPosition);
        }
        onChange === null || onChange === void 0 ? void 0 : onChange({
            value: formattedNumber.replace(/[^0-9]+/g, ''),
            data: getCountryData(),
            formattedValue: formattedNumber,
        });
    }, [formattedNumber, selectedCountry]);
    const handleInputClick = (e) => {
        setShowDropdownState(false);
        onClick === null || onClick === void 0 ? void 0 : onClick(e, getCountryData());
    };
    const handleDoubleClick = (e) => {
        const len = e.target.value.length;
        e.target.setSelectionRange(0, len);
    };
    const handleFlagItemClick = (_country, e) => {
        const currentSelectedCountry = selectedCountry;
        const newSelectedCountry = onlyCountriesState.find((o) => o === _country);
        if (!newSelectedCountry)
            return;
        const unformattedNumber = formattedNumber.replace(' ', '').replace('(', '').replace(')', '').replace('-', '');
        const newNumber = unformattedNumber.length > 1
            ? unformattedNumber.replace(currentSelectedCountry.dialCode, newSelectedCountry.dialCode)
            : newSelectedCountry.dialCode;
        const _formattedNumber = formatNumber(newNumber.replace(/\D/g, ''), newSelectedCountry);
        setFormattedNumber(_formattedNumber);
        setSelectedCountry(newSelectedCountry);
        setShowDropdownState(false);
        setFreezeSelection(true);
        setSearchValue('');
        cursorToEnd();
        onChange === null || onChange === void 0 ? void 0 : onChange({
            value: _formattedNumber.replace(/[^0-9]+/g, ''),
            data: getCountryData(),
            event: e,
            formattedValue: _formattedNumber,
        });
    };
    const handleInputFocus = (e) => {
        if (numberInputRef.current) {
            if (numberInputRef.current.value === prefix && selectedCountry && !disableCountryCode) {
                setFormattedNumber(prefix + selectedCountry.dialCode);
                if (jumpCursorToEnd)
                    setTimeout(cursorToEnd, 0);
            }
        }
        onFocus === null || onFocus === void 0 ? void 0 : onFocus(e, getCountryData());
        if (jumpCursorToEnd)
            setTimeout(cursorToEnd, 0);
    };
    const handlePast = (e) => {
        e.preventDefault();
        const pastedText = e.clipboardData.getData('text/plain');
        setFormattedNumber(pastedText);
        const newSelectedCountry = guessSelectedCountry(pastedText.substring(0, 6).replace('+', '')) || selectedCountry;
        setSelectedCountry(newSelectedCountry);
    };
    const handleInputBlur = (e) => {
        onBlur === null || onBlur === void 0 ? void 0 : onBlur(e, getCountryData());
    };
    const handleInputCopy = (e) => {
        if (!copyNumbersOnly)
            return;
        const text = window
            .getSelection()
            .toString()
            .replace(/[^0-9]+/g, '');
        e.clipboardData.setData('text/plain', text);
        e.preventDefault();
    };
    const getSearchFilteredCountries = () => {
        const _enableSearch = enableSearch || true;
        const allCountries = concatPreferredCountries(preferredCountriesState, onlyCountriesState);
        const sanitizedSearchValue = searchValue.trim().toLowerCase().replace('+', '');
        if (_enableSearch && sanitizedSearchValue) {
            if (/^\d+$/.test(sanitizedSearchValue)) {
                return allCountries.filter(({ dialCode }) => [`${dialCode}`].some((field) => field.toLowerCase().includes(sanitizedSearchValue)));
            }
            const iso2countries = allCountries.filter(({ iso2 }) => [`${iso2}`].some((field) => field.toLowerCase().includes(sanitizedSearchValue)));
            const searchedCountries = allCountries.filter(({ name, localName }) => [`${name}`, `${localName || ''}`].some((field) => field.toLowerCase().includes(sanitizedSearchValue)));
            return [...new Set([].concat(iso2countries, searchedCountries))];
        }
        return allCountries;
    };
    const getHighlightCountryIndex = (direction) => {
        const _highlightCountryIndex = highlightCountryIndex + direction;
        if (_highlightCountryIndex < 0 ||
            _highlightCountryIndex >= onlyCountriesState.length + preferredCountriesState.length) {
            return _highlightCountryIndex - direction;
        }
        if (enableSearch && _highlightCountryIndex > getSearchFilteredCountries().length)
            return 0;
        return _highlightCountryIndex;
    };
    const searchCountry = () => {
        const probableCandidate = getProbableCandidate() || onlyCountriesState[0];
        const probableCandidateIndex = onlyCountriesState.findIndex((o) => o === probableCandidate) + preferredCountriesState.length;
        setQueryString('');
        setHighlightCountryIndex(probableCandidateIndex);
    };
    const handleKeydown = (e) => {
        const { currentTarget: { className }, } = e;
        if (className.includes('selected-flag') && e.which === keys.ENTER && !showDropdown) {
            return handleFlagDropdownClick(e);
        }
        if (className.includes('form-control') && (e.which === keys.ENTER || e.which === keys.ESC)) {
            return e.target.blur();
        }
        if (!showDropdown || disabled)
            return;
        if (className.includes('search-box')) {
            if (e.which !== keys.UP && e.which !== keys.DOWN && e.which !== keys.ENTER) {
                if (e.which === keys.ESC && e.target.value === '') {
                }
                else {
                    return;
                }
            }
        }
        if (e.preventDefault) {
            e.preventDefault();
        }
        const moveHighlight = (direction) => {
            setHighlightCountryIndex(getHighlightCountryIndex(direction));
        };
        switch (e.which) {
            case keys.DOWN:
                moveHighlight(1);
                break;
            case keys.UP:
                moveHighlight(-1);
                break;
            case keys.ENTER:
                if (enableSearch) {
                    handleFlagItemClick(getSearchFilteredCountries()[highlightCountryIndex] || getSearchFilteredCountries()[0], e);
                }
                else {
                    handleFlagItemClick([...preferredCountriesState, ...onlyCountriesState][highlightCountryIndex], e);
                }
                break;
            case keys.ESC:
            case keys.TAB:
                setShowDropdownState(false);
                cursorToEnd();
                break;
            default:
                if ((e.which >= keys.A && e.which <= keys.Z) || e.which === keys.SPACE) {
                    setQueryString(queryString + String.fromCharCode(e.which));
                    (0, lodash_1.debounce)(searchCountry, 250)();
                }
        }
    };
    const handleInputKeyDown = (e) => {
        if (e.which === keys.ENTER) {
            if (onEnterKeyPress)
                onEnterKeyPress(e);
        }
        if (onKeyDown)
            onKeyDown(e);
    };
    const handleSearchChange = (e) => {
        const { currentTarget: { value: _searchValue }, } = e;
        let _highlightCountryIndex = 0;
        if (searchValue === '' && selectedCountry) {
            _highlightCountryIndex = concatPreferredCountries(preferredCountriesState, onlyCountriesState).findIndex((o) => o === selectedCountry);
        }
        setSearchValue(_searchValue);
        setHighlightCountryIndex(_highlightCountryIndex);
    };
    const isValidValue = (0, react_1.useMemo)(() => {
        if (typeof isValid === 'boolean') {
            return isValid;
        }
        if (isValid && typeof isValid === 'function') {
            const isValidProcessed = isValid(formattedNumber.replace(/\D/g, ''), selectedCountry, onlyCountriesState, hiddenAreaCodesState);
            if (typeof isValidProcessed === 'boolean') {
                return isValidProcessed;
            }
            return false;
        }
    }, [isValid, formattedNumber]);
    return ((0, jsx_runtime_1.jsxs)("div", { children: [(0, jsx_runtime_1.jsxs)(material_1.Box, { className: (0, classnames_1.default)({
                    [containerClass]: true,
                }), style: containerStyle, component: "div", sx: reactTelInputStyles, onKeyDown: handleKeydown, children: [(0, jsx_runtime_1.jsx)(material_1.Box, Object.assign({ component: "input", className: (0, classnames_1.default)({
                            'form-control': true,
                            'invalid-number': !isValidValue,
                            open: showDropdown,
                            [inputClass]: true,
                        }), sx: formControlStyles, style: inputStyle, onChange: handleInput, onClick: handleInputClick, onDoubleClick: handleDoubleClick, onFocus: handleInputFocus, onBlur: handleInputBlur, onCopy: handleInputCopy, onPaste: handlePast, value: formattedNumber, onKeyDown: handleInputKeyDown, placeholder: placeholder, disabled: disabled, type: "tel" }, inputProps, { ref: (el) => {
                            numberInputRef.current = el;
                        } })), formattedNumber !== '' && !hideClearButton && ((0, jsx_runtime_1.jsx)(icons_material_1.HighlightOffOutlined, { sx: {
                            position: 'absolute',
                            right: '12px',
                            width: '20px',
                            height: '20px',
                            color: !isValid
                                ? theme_1.theme.qlub.palette.danger.main
                                : formattedNumber.length > 0
                                    ? theme_1.theme.qlub.palette.onSurfaceColors.mediumEmphasis
                                    : theme_1.theme.qlub.palette.onSurfaceColors.disabled,
                            cursor: formattedNumber.length > 0 ? 'pointer' : 'default',
                        }, onClick: () => {
                            const defaultCountry = onlyCountriesState.find((o) => o.iso2 === country) || 0;
                            setSelectedCountry(defaultCountry);
                            setFormattedNumber(disableCountryCode ? '' : formatNumber(defaultCountry.dialCode, defaultCountry));
                        } })), (0, jsx_runtime_1.jsx)(material_1.Box, { component: "div", className: (0, classnames_1.default)({
                            'flag-dropdown': true,
                            'invalid-number': !isValidValue,
                            open: showDropdown,
                            [buttonClass]: true,
                        }), sx: flagDropdownStyles, style: buttonStyle, ref: (el) => {
                            dropdownContainerRef.current = el;
                        }, children: (0, jsx_runtime_1.jsx)(CountrySelector_1.default, { selectedCountry: selectedCountry, countries: getSearchFilteredCountries(), defaultCountry: "tr", onChange: (_country, event) => handleFlagItemClick(_country, event), drawerTitle: drawerTitle, searchHandler: handleSearchChange, lang: lang, disableDropdown: disableDropdown, searchInputPlaceHolder: searchInputPlaceHolder }) })] }), !isValid && ((0, jsx_runtime_1.jsx)(Typography_1.default, { typo: "caption_overline", sx: { mt: '4px', color: theme_1.theme.qlub.palette.danger.main, px: '12px' }, children: defaultErrorMessage || 'Supporting text' }))] }));
};
exports.PhoneInput = PhoneInput;
exports.default = exports.PhoneInput;
